import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, CircularProgress, IconButton,
  Tooltip, Card, CardContent, Grid, InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  QrCode as QrCodeIcon,
  Phone as PhoneIcon,
  Telegram as TelegramIcon,
  LocalShipping as DeliveryIcon,
  Store as StoreIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
  ChatBubbleOutline as ChatIcon,
} from '@mui/icons-material';
import api from '../api/api';
import EmployeePickupCode from './EmployeePickupCode';
import usePortalChat from '../../hooks/usePortalChat';

const STATUS_CONFIG = {
  'заявка': { label: 'Заявка', color: 'warning' },
  'оплачена': { label: 'Оплачена', color: 'info' },
  'в_процессе': { label: 'Активна', color: 'success' },
  'активная': { label: 'Активна', color: 'success' },
  'активна': { label: 'Активна', color: 'success' },
  'выдана': { label: 'Выдана', color: 'success' },
  'active': { label: 'Активна', color: 'success' },
  'просрочена': { label: 'Просрочена', color: 'warning' },
  'отменена': { label: 'Отменена', color: 'error' },
  ...Object.fromEntries(['закрыта', 'закрытая', 'завершена', 'completed'].map(status => [status, { label: 'Завершена', color: 'default' }])),
};

const ApplicationsPage = () => {
  const { openChat, opening: chatOpening, error: chatError } = usePortalChat();
  const mounted = useRef(true);
  const loadVersion = useRef(0);
  const activatePending = useRef(false);
  const cancelPending = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; loadVersion.current += 1; };
  }, []);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activateDialog, setActivateDialog] = useState(false);
  const [pickupCode, setPickupCode] = useState('');
  const [activating, setActivating] = useState(false);
  const [activateResult, setActivateResult] = useState(null);
  const [filterStatus, setFilterStatus] = useState(''); // '' = заявка+оплачена
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const syncPending = useRef(false);
  const [syncMessage, setSyncMessage] = useState('');

  const loadApplications = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setError(null);
    try {
      const params = filterStatus ? { status: filterStatus } : {};
      const response = await api.get('/applications', { params });
      if (version !== loadVersion.current) return;
      const items = response.data.items || [];
      if (!Array.isArray(items)) throw new Error('Invalid application list');
      setApplications(items.filter(Boolean));
    } catch (err) {
      if (version !== loadVersion.current) return;
      console.error('Ошибка загрузки заявок:', err);
      setError('Ошибка загрузки заявок');
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }, [filterStatus]);

  const handleSync = async (application) => {
    if (syncPending.current) return;
    syncPending.current = true;
    setSyncing(application.id);
    setSyncMessage('');
    setError(null);
    try {
      const { data } = await api.post(`/applications/${application.id}/sync-payment`);
      if (!mounted.current) return;
      await loadApplications();
      if (!mounted.current) return;
      setSyncMessage(data.results?.some(result => result.status === 'not_found')
        ? `Ozon не находит платёж заказа №${application.deal_number}. Требуется проверка в банке.`
        : `Заказ №${application.deal_number}: сверка с банком выполнена.`);
    } catch (err) {
      if (mounted.current) setError(err.response?.data?.detail || 'Не удалось сверить оплату с банком');
    } finally {
      syncPending.current = false;
      if (mounted.current) setSyncing(null);
    }
  };

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleActivate = async () => {
    if (pickupCode.length !== 6 || activatePending.current) return;
    activatePending.current = true;
    setActivating(true);
    setActivateResult(null);
    try {
      const response = await api.post('/applications/activate', { code: pickupCode });
      if (!mounted.current) return;
      setActivateResult({ success: true, message: response.data.message });
      setPickupCode('');
      loadApplications(); // Обновляем список
    } catch (err) {
      if (!mounted.current) return;
      const detail = err.response?.data?.detail || 'Ошибка активации';
      setActivateResult({ success: false, message: detail });
    } finally {
      activatePending.current = false;
      if (mounted.current) setActivating(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget || cancelPending.current) return;
    cancelPending.current = true;
    setCancelling(true);
    try {
      await api.post(`/applications/${cancelTarget.id}/cancel`);
      if (!mounted.current) return;
      setCancelDialog(false);
      setCancelTarget(null);
      loadApplications();
    } catch (err) {
      if (!mounted.current) return;
      console.error('Ошибка отмены:', err);
      alert(err.response?.data?.detail || 'Ошибка при отмене заявки');
    } finally {
      cancelPending.current = false;
      if (mounted.current) setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    if (dateStr.includes('T')) {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('ru-RU') + ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }
    return dateStr;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Шапка */}
      <Box className="erp-section-heading" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          📋 Онлайн-заявки
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<QrCodeIcon />}
            onClick={() => { setActivateDialog(true); setActivateResult(null); setPickupCode(''); }}
          >
            Ввести код получения
          </Button>
          <IconButton aria-label="Обновить заявки" onClick={loadApplications} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {chatError && <Alert severity="error" sx={{ mb: 2 }}>{chatError}</Alert>}
      {/* Статистика */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="warning.main">
                {applications.filter(a => a.status === 'заявка').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Новых заявок</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="info.main">
                {applications.filter(a => a.status === 'оплачена').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Оплачено (ждут выдачи)</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="success.main">
                {applications.reduce((sum, a) => sum + (Number(a.paid_total ?? a.details?.paid_amount) || 0), 0).toLocaleString('ru-RU')} ₽
              </Typography>
              <Typography variant="body2" color="text.secondary">Сумма оплат</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Показатели относятся к выбранному списку. Возвращено: {applications.reduce((sum, a) => sum + (Number(a.refunded_total) || 0), 0).toLocaleString('ru-RU')} ₽.
        {' '}Осталось вернуть по отменённым заявкам: {applications.reduce((sum, a) => sum + (Number(a.refund_due) || 0), 0).toLocaleString('ru-RU')} ₽.
      </Typography>

      {/* Фильтры */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap:'wrap' }}>
        {[
          { value: '', label: 'Активные (заявка + оплачена)' },
          { value: 'заявка', label: 'Только заявки' },
          { value: 'оплачена', label: 'Только оплаченные' },
          { value: 'issued', label: 'Выданные' },
          { value: 'completed', label: 'Завершённые' },
          { value: 'отменена', label: 'Отменённые' },
          { value: 'all', label: 'Все онлайн-заказы' },
        ].map(f => (
          <Chip
            key={f.value}
            label={f.label}
            variant={filterStatus === f.value ? 'filled' : 'outlined'}
            color={filterStatus === f.value ? 'primary' : 'default'}
            onClick={() => setFilterStatus(f.value)}
          />
        ))}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {syncMessage && <Alert severity="info" sx={{ mb: 2 }}>{syncMessage}</Alert>}

      {/* Таблица */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : applications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {filterStatus ? 'Нет заказов в выбранном списке' : 'Нет активных заявок'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Здесь сохраняются заказы, оформленные онлайн. Для истории выберите «Все онлайн-заказы».
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>№</strong></TableCell>
                <TableCell><strong>Дата</strong></TableCell>
                <TableCell><strong>Клиент</strong></TableCell>
                <TableCell><strong>Товар</strong></TableCell>
                <TableCell><strong>Сумма</strong></TableCell>
                <TableCell><strong>Статус</strong></TableCell>
                <TableCell><strong>Доставка</strong></TableCell>
                <TableCell><strong>Оферта</strong></TableCell>
                <TableCell><strong>Оплата</strong></TableCell>
                <TableCell><strong>Код</strong></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((app) => {
                const d = app.details || {};
                const statusConf = STATUS_CONFIG[app.status] || { label: app.status, color: 'default' };
                return (
                  <TableRow key={app.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {app.deal_number}
                      </Typography>
                      {d.source === 'telegram' && (
                        <Tooltip title="Из Telegram">
                          <TelegramIcon sx={{ fontSize: 14, color: '#0088cc', ml: 0.5 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(app.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {app.client ? (
                        <>
                          <Typography variant="body2">{app.client.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            <PhoneIcon sx={{ fontSize: 12, mr: 0.3 }} />
                            {app.client.phone}
                          </Typography>
                        </>
                      ) : '—'}
                    </TableCell>
                    <TableCell>{app.asset?.name || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {app.amount?.toLocaleString('ru-RU')} ₽
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusConf.label}
                        color={statusConf.color}
                      />
                    </TableCell>
                    <TableCell>
                      {d.delivery_type === 'delivery' ? (
                        <Tooltip title={d.delivery_address || ''}>
                          <Chip size="small" icon={<DeliveryIcon />} label="Доставка" variant="outlined" />
                        </Tooltip>
                      ) : (
                        <Chip size="small" icon={<StoreIcon />} label="Самовывоз" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {d.offer_accepted ? (
                        <Tooltip title={d.offer_accepted_at ? `Принята ${formatDate(d.offer_accepted_at)}` : ''}>
                          <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        </Tooltip>
                      ) : (
                        <ScheduleIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {Number(app.paid_total ?? d.paid_amount) > 0 || d.payment_status === 'succeeded' ? (
                        <Tooltip title={`${d.paid_amount?.toLocaleString('ru-RU')} ₽ — ${formatDate(d.paid_at)}`}>
                          <Chip size="small" icon={<PaymentIcon />} label="Оплачено" color="success" variant="outlined" />
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Ожидает</Typography>
                      )}
                      {Number(app.paid_total) > 0 && <Typography variant="caption" display="block">Получено: {Number(app.paid_total).toLocaleString('ru-RU')} ₽</Typography>}
                      {Number(app.paid_total) > 0 && <Typography variant="caption" display="block">Возвращено: {Number(app.refunded_total || 0).toLocaleString('ru-RU')} ₽</Typography>}
                      {Number(app.refund_due) > 0 && <Chip size="small" color="warning" label={`Осталось вернуть: ${Number(app.refund_due).toLocaleString('ru-RU')} ₽`} />}
                      {Number(app.refunded_total) > 0 && <Chip size="small" color="success" variant="outlined" label={Number(app.refunded_total) >= Number(app.paid_total) ? 'Полный возврат' : 'Частичный возврат'} />}
                      {app.can_sync_payment && <Box sx={{ mt: 0.5 }}>
                        <Button size="small" disabled={syncing !== null} onClick={() => handleSync(app)}>
                          {syncing === app.id ? 'Проверяем банк…' : 'Сверить с банком'}
                        </Button>
                        {app.payment_checked_at && <Typography variant="caption" display="block" color="text.secondary">Проверено: {formatDate(app.payment_checked_at)}</Typography>}
                      </Box>}
                    </TableCell>
                    <TableCell>
                      {d.pickup_code_used ? (
                        <Chip size="small" label="Выдано ✓" color="default" />
                      ) : app.status === 'оплачена' && !app.refunded_total && d.pickup_secret_active && d.contract_token ? (
                        <EmployeePickupCode token={d.contract_token} />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {app.client && <Button
                        size="small" startIcon={<ChatIcon />} disabled={chatOpening}
                        onClick={() => openChat({ dealId: app.id })}
                      >
                        Написать
                      </Button>}
                      {(app.status === 'заявка' || app.status === 'оплачена') && (
                        <Tooltip title="Отменить заявку">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => { setCancelTarget(app); setCancelDialog(true); }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Диалог подтверждения отмены */}
      <Dialog open={cancelDialog} onClose={() => !cancelling && setCancelDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Отменить заявку?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Заявка <strong>#{cancelTarget?.deal_number}</strong> ({cancelTarget?.client?.name || '—'}) будет отменена.
            Это действие нельзя отменить.
          </Typography>
          {Number(cancelTarget?.paid_total ?? cancelTarget?.details?.paid_amount) > 0 && <Alert severity="warning" sx={{ mt: 2 }}>
            Отмена заявки не возвращает деньги. Оформите возврат в банке, затем нажмите «Сверить с банком» в списке отменённых заказов.
          </Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)} disabled={cancelling}>Назад</Button>
          <Button
            variant="contained"
            color="error"
            disabled={cancelling}
            onClick={handleCancelConfirm}
            startIcon={cancelling ? <CircularProgress size={18} /> : <CancelIcon />}
          >
            {cancelling ? 'Отменяем...' : 'Отменить заявку'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог ввода кода получения */}
      <Dialog open={activateDialog} onClose={() => !activating && setActivateDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>🔑 Ввести код получения</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Клиент называет 6-значный код. Введите его для активации сделки и выдачи товара.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Код получения"
            disabled={activating}
            value={pickupCode}
            onChange={e => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setPickupCode(val);
            }}
            placeholder="000000"
            InputProps={{
              startAdornment: <InputAdornment position="start"><QrCodeIcon /></InputAdornment>,
              style: { fontSize: 28, letterSpacing: 8, fontFamily: 'monospace', textAlign: 'center' },
            }}
            inputProps={{ maxLength: 6, style: { textAlign: 'center' } }}
            sx={{ mt: 1 }}
          />
          {activateResult && (
            <Alert
              severity={activateResult.success ? 'success' : 'error'}
              sx={{ mt: 2 }}
            >
              {activateResult.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={activating} onClick={() => setActivateDialog(false)}>Закрыть</Button>
          <Button
            variant="contained"
            color="success"
            disabled={pickupCode.length !== 6 || activating}
            onClick={handleActivate}
            startIcon={activating ? <CircularProgress size={18} /> : <CheckIcon />}
          >
            {activating ? 'Проверяем...' : 'Активировать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApplicationsPage;
